import { useEffect, useRef } from 'react';

import {
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import {
  BaseSectionOverview,
  SectionOrSubSection,
} from 'approot/activity-builder/activity-builder.constants';
import ActivityPrint from 'approot/activity-builder/components/activity/activity-print';
import { ActivityBuilderIntroFooter } from 'approot/activity-builder/introduction/activity-builder-intro';
import ActivityAnswersPrintHeader from './activity-answers-print-header';
import ActivityAnswersPrintComponents from './activity-answers-print-components';
import { getComponentsOfType } from 'approot/activity-builder/components/shared/logic/get-components-of-type';
import {
  ComponentDraw,
  ComponentFillInBlanks,
} from 'approot/activity-builder/components/component.constants';
import {
  ActivityAnswersPageInner,
  ActivityAnswersPageInnerContent,
} from './activity-answers.style';
import { PRINT } from './activity-answers-print.constants';

type Props = {
  index: number;
  section: SectionOrSubSection;
  includesIntro: boolean;
  unitName: string;
  contentName: string;
  userName: string;
  subject: string;
  sectionsOverview: BaseSectionOverview[];
  language?: LanguageCodes;
  studentId?: number;
  onRenderComplete?: () => void;
  contentVersion: string | null | undefined;
};

const ActivityAnswerPrintSection = ({
  index,
  section,
  includesIntro,
  unitName,
  contentName,
  userName,
  subject,
  sectionsOverview,
  language = LANGUAGE_CODE_EN_AU,
  studentId,
  onRenderComplete,
  contentVersion,
}: Props) => {
  const totalResponseComponents = useRef<number>(
    getComponentsOfType<ComponentDraw | ComponentFillInBlanks>({
      components: section.components,
      types: [
        ACTIVITIES_COMPONENT_TYPE_DRAW,
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
      ],
    }).length
  );
  const completedCount = useRef<number>(0);

  useEffect(() => {
    // if no components, we can render the section immediately
    if (totalResponseComponents.current === 0) {
      onRenderComplete?.();
    }
  }, [section, onRenderComplete]);

  return (
    <ActivityAnswersPageInner key={section.id}>
      <ActivityAnswersPrintHeader
        unitName={unitName}
        contentName={contentName}
        userName={userName}
      />
      <ActivityAnswersPageInnerContent>
        <ActivityPrint section={section} sectionsOverview={sectionsOverview} />
        <ActivityAnswersPrintComponents
          previewType={PRINT}
          section={section}
          studentId={studentId}
          language={language}
          onRenderComplete={() => {
            if (totalResponseComponents.current > 0) {
              completedCount.current++;
              if (completedCount.current === totalResponseComponents.current) {
                onRenderComplete?.();
              }
            } else {
              onRenderComplete?.();
            }
          }}
          contentVersion={contentVersion}
        />
      </ActivityAnswersPageInnerContent>
      <ActivityBuilderIntroFooter
        pageNumber={index + (includesIntro ? 2 : 1)}
        subject={subject}
        showGoogleAttribution={language !== LANGUAGE_CODE_EN_AU}
      />
    </ActivityAnswersPageInner>
  );
};

export default ActivityAnswerPrintSection;
